import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import TopicCard from "@/components/feature/TopicCard";
import useSeo from "@/hooks/useSeo";
import {
  SITE_NAME,
  SITE_URL,
  formatDate,
  getCategoryLabel,
  getRelatedTopics,
  getTopicById,
} from "@/lib/site";
import TopicHero from "./components/TopicHero";
import TopicTimeline from "./components/TopicTimeline";
import TopicSidebar from "./components/TopicSidebar";

export default function TopicDetail() {
  const { id } = useParams<{ id: string }>();
  const topic = id ? getTopicById(id) : undefined;
  const related = topic ? getRelatedTopics(topic, 3) : [];

  useSeo({
    title: topic
      ? `${topic.title}_进展与后续追踪｜瓜田雷达`
      : "未找到该事件档案｜瓜田雷达",
    description: topic
      ? `${topic.summary} 当前状态：${topic.status}，最近更新于 ${formatDate(topic.updatedAt)}。查看该事件从曝光到回应的完整进展时间线与关键节点。`
      : "该事件档案不存在或已调整，返回话题档案浏览其它正在追踪的网曝吃瓜进展与后续动态。",
    keywords: topic
      ? [...topic.tags, "事件进展", "后续追踪", "网曝吃瓜"].join(",")
      : "话题档案,事件进展,网曝吃瓜",
    path: topic ? `/topic/${topic.id}` : "/topics",
    breadcrumbs: topic
      ? [
          { name: "首页", path: "/" },
          { name: "话题档案", path: "/topics" },
          { name: topic.title, path: `/topic/${topic.id}` },
        ]
      : [
          { name: "首页", path: "/" },
          { name: "话题档案", path: "/topics" },
        ],
    jsonLd: topic
      ? {
          "@type": "Article",
          headline: topic.title,
          description: topic.summary,
          url: `${SITE_URL}/topic/${topic.id}`,
          mainEntityOfPage: `${SITE_URL}/topic/${topic.id}`,
          datePublished: topic.publishedAt,
          dateModified: topic.updatedAt,
          inLanguage: "zh-CN",
          articleSection: getCategoryLabel(topic.categoryId),
          keywords: topic.tags.join(","),
          image: topic.cover,
          author: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
          publisher: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
        }
      : undefined,
  });

  if (!topic) {
    return (
      <>
        <SiteHeader />
        <main className="bg-background-50">
          <div className="flex flex-col items-center px-4 py-20 text-center md:py-28">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-600">
              <i className="ri-file-unknow-line" aria-hidden="true" />
            </span>
            <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">
              没有找到这份档案
            </h1>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
              该事件可能已合并到其它档案，或者编号有误。你可以回到话题档案，用关键词重新查找。
            </p>
            <Link
              to="/topics"
              className="mt-7 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-archive-2-line" aria-hidden="true" />
              返回话题档案
            </Link>
          </div>
        </main>
        <SiteFooter />
      </>
    );
  }

  return (
    <>
      <SiteHeader />
      <main>
        <TopicHero topic={topic} />

        <section className="bg-background-50">
          <div className="px-4 py-10 md:px-8 md:py-14">
            <div className="grid gap-10 lg:grid-cols-[1fr_320px]">
              <article>
                <section id="overview" className="scroll-mt-32">
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    事件综述
                  </h2>
                  <div className="mt-4 space-y-4">
                    {topic.body.map((paragraph) => (
                      <p
                        key={paragraph.slice(0, 24)}
                        className="text-sm leading-[1.9] text-foreground-700 md:text-[15px]"
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </section>

                <section
                  id="keypoints"
                  className="mt-10 scroll-mt-32 rounded-lg border border-background-200 bg-background-100 p-6"
                >
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    关键节点速览
                  </h2>
                  <p className="mt-2 text-sm text-foreground-600">
                    先看这三到四条，就能抓住这件事的主干。
                  </p>
                  <ul className="mt-5 space-y-3">
                    {topic.keyPoints.map((point) => (
                      <li key={point.slice(0, 20)} className="flex gap-3">
                        <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500" aria-hidden="true" />
                        <span className="text-sm leading-relaxed text-foreground-700">{point}</span>
                      </li>
                    ))}
                  </ul>
                </section>

                <section id="timeline" className="mt-10 scroll-mt-32">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                      完整时间线
                    </h2>
                    <span className="text-xs text-foreground-500">
                      自上而下为最近发生的进展
                    </span>
                  </div>
                  <div className="mt-6">
                    <TopicTimeline topic={topic} />
                  </div>
                </section>

                <section
                  id="records"
                  className="mt-10 scroll-mt-32 rounded-lg border border-background-200 bg-background-100 p-6"
                >
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    记录说明
                  </h2>
                  <div className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-600">
                    <p>
                      本档案只整理可追溯的公开信息，节点按发生时间排列。涉及互相矛盾的说法时，平台并列呈现而不作取舍，也不会对任何一方作出评价或定性。
                    </p>
                    <p>
                      档案当前状态为「{topic.status}」。状态会随事件推进而变化，更新记录会保留在时间线中，方便你对照此前的判断。
                    </p>
                    <p>
                      如果你发现内容与公开信息不符，可通过页面下方的纠错入口说明情况，我们会核对原始出处后更新档案。
                    </p>
                  </div>
                  <div className="mt-5 space-y-2 border-t border-background-200 pt-4 text-xs text-foreground-500">
                    <p>关键词：{topic.tags.join(" / ")}</p>
                    <p>
                      首次曝光 {formatDate(topic.publishedAt)} · 最近更新{" "}
                      {formatDate(topic.updatedAt)}
                    </p>
                  </div>
                </section>
              </article>

              <TopicSidebar topic={topic} />
            </div>
          </div>
        </section>

        <section className="border-t border-background-200 bg-background-100">
          <div className="px-4 py-10 md:px-8 md:py-14">
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="flex items-center gap-2 text-xs font-label uppercase tracking-[0.2em] text-primary-600">
                  <span className="inline-block h-[2px] w-6 bg-primary-500" aria-hidden="true" />
                  Related
                </p>
                <h2 className="mt-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  相关事件档案
                </h2>
              </div>
              <Link
                to="/topics"
                className="inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
              >
                查看全部档案
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <TopicCard key={item.id} topic={item} />
              ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}